// Void reasons (parity backoffice pages/settings-order-staff-timecards.md §1.6, SCR-148/DLG-089; field note
// backoffice.settings.order-checkout.voids): the location's list of reasons the register offers in Void Order and Delete
// Items, with "Other" (free text) always added by the register. Location config written at the node or in the cloud, one
// `ConfigFieldsWritten` fact per write, like departments. A void copies the reason's text, so deleting or renaming a
// reason never changes a past void.
import { z } from 'zod';
import { defineCommand, defineQuery } from '../../define/operations.ts';
import { LocationId, VoidReasonId } from '../../primitives/codecs.ts';
import { CloudAuthoritative, ConfigFieldsWritten, ProposalRequired } from '../config/index.ts';
import { CatalogEntityNotFound, CatalogInvalid } from './errors.ts';

/** Legacy `void_reasons.reason` (required, trimmed; legacy had no length cap, 255 is its column). */
export const VoidReasonFields = z.object({ reason: z.string().trim().min(1).max(255) }).meta({ id: 'VoidReasonFields' });
export const VoidReasonView = VoidReasonFields.extend({ voidReasonId: VoidReasonId, locationId: LocationId.nullable(), version: z.int().nonnegative() })
  .meta({ id: 'VoidReasonView' });

const common = {
  availability: 'location', origin: 'any', emits: [ConfigFieldsWritten],
  errors: [CatalogInvalid, CloudAuthoritative, ProposalRequired, CatalogEntityNotFound], dryRun: true,
} as const;

export const UpsertVoidReason = defineCommand({
  ...common,
  name: 'catalog.upsertVoidReason',
  summary: 'Add a void reason or change its text.',
  input: z.object({ voidReasonId: VoidReasonId, locationId: LocationId, changes: VoidReasonFields.partial() }),
  output: VoidReasonView,
  permission: 'settings.order_checkout.manage',
  risk: 'low',
  agent: { expose: true, surfaces: ['backoffice.assistant'] },
  legacy: ['POST /v1/void-reasons', 'PUT /v1/void-reasons/{id}'],
});

export const DeleteVoidReason = defineCommand({
  ...common,
  name: 'catalog.deleteVoidReason',
  summary: 'Delete a void reason; past voids keep the text they recorded.',
  input: z.object({ voidReasonId: VoidReasonId, locationId: LocationId }),
  output: z.object({ id: VoidReasonId, deleted: z.literal(true) }),
  permission: 'settings.order_checkout.manage',
  risk: 'low',
  agent: { expose: false },
  legacy: ['DELETE /v1/void-reasons/{id}'],
});

export const ListVoidReasons = defineQuery({
  availability: 'location', scope: 'location', errors: [], agent: { expose: true, surfaces: ['backoffice.assistant'] },
  // Legacy's GET /v1/void-reasons sits behind manage_order_checkout_settings, as its page does (lane P2, item 6).
  permission: 'settings.order_checkout.manage',
  name: 'catalog.voidReasons.list',
  summary: "The location's void reasons, oldest first (the order the register shows them).",
  input: z.object({ locationId: LocationId }),
  output: z.object({ voidReasons: z.array(VoidReasonView) }),
  legacy: ['GET /v1/void-reasons'],
});

export const voidReasonCommands = [UpsertVoidReason, DeleteVoidReason] as const;
export const voidReasonQueries = [ListVoidReasons] as const;
